import React from 'react'
import {Accordion, Badge, Button, Col, Form, InputGroup, Row} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {
  immigrationStatusLabel as catalogImmigrationStatusLabel,
  translatedImmigrationStatuses,
} from '../../../../../helpers/catalogMessages'
import {TFormMemberPolicy} from '../../../../policies/policiesTypes'
import {DateInput} from 'app/tfi/components/DateInput'
import {copyToClipboard} from '../../../../../../../utils/clipboardUtils'

interface LegalDocumentAccordionItemProps {
  eventKey: string
  membernum: number
  values: TFormMemberPolicy
  handleChange: (field: string, value: string) => void
  immigrationStatusLabel?: string
  formattedSSN?: string
}

export const LegalDocumentAccordionItem: React.FC<LegalDocumentAccordionItemProps> = ({
  eventKey,
  membernum,
  values,
  handleChange,
  immigrationStatusLabel,
  formattedSSN,
}) => {
  const intl = useIntl()
  const getImmigrationStatusLabel = (status: string | null | undefined) =>
    catalogImmigrationStatusLabel(intl, status)

  const displayStatus = getImmigrationStatusLabel(values.immigrationStatus) || immigrationStatusLabel
  const handleCopySSN = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    copyToClipboard(values.ssn, 'SSN')
  }

  return (
    <Accordion.Item eventKey={eventKey}>
      <Accordion.Header>
        <div style={{ width: '100%' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '2px', fontSize: '0.95rem', lineHeight: '1.2' }}>
            <FormattedMessage
              id='policies.detail.form.member.section.legalInfo'
              defaultMessage='Legal information'
            />
          </div>
          <div style={{ fontSize: '0.85rem', color: '#666', display: 'flex', flexWrap: 'wrap', gap: '4px 8px', alignItems: 'center', lineHeight: '1.2' }}>
            {displayStatus && (
              <Badge bg="secondary" style={{ fontSize: '0.7rem', padding: '2px 6px' }}>
                <i className="bi bi-shield-check" style={{ marginRight: '3px', fontSize: '0.7rem' }}></i>
                {displayStatus}
              </Badge>
            )}
            {values.ssn && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <i className="bi bi-credit-card-2-front" style={{ fontSize: '0.85rem' }}></i>
                <span>
                  <FormattedMessage
                    id='policies.detail.form.member.legal.ssnSummary'
                    defaultMessage='SSN: {ssn}'
                    values={{ssn: values.ssn}}
                  />
                </span>
                <Button
                  variant="link"
                  size="sm"
                  style={{ padding: 0, border: 0, textDecoration: 'none', fontSize: '0.85rem' }}
                  title={intl.formatMessage(
                    {id: 'policies.detail.form.holder.copyField', defaultMessage: 'Copy {field}'},
                    {field: intl.formatMessage({id: 'policies.list.preview.table.ssn', defaultMessage: 'SSN'})}
                  )}
                  onClick={handleCopySSN}
                >
                  <i className="bi bi-files text-primary"></i>
                </Button>
              </span>
            )}
          </div>
        </div>
      </Accordion.Header>
    <Accordion.Body>
      <div>
        <Row>
          <Col lg={6} sm={12} md={4}>
            <Form.Group>
              <Form.Label htmlFor='immigrationStatus'>
                <FormattedMessage
                  id='policies.list.wizard.holder.immigrationStatus'
                  defaultMessage='Immigration Status'
                />
              </Form.Label>
              <Form.Select
                id='immigrationStatus'
                name='immigrationStatus'
                value={values.immigrationStatus ?? ''}
                onChange={(e) => handleChange('immigrationStatus', e.target.value)}
                // onChange={(e) => {
                //   if (membernum === 0) handleChange('holder.immigrationStatus', e.target.value)
                //   else handleChange(`member[${membernum}].immigrationStatus`, e.target.value)
                // }}
                aria-label={intl.formatMessage({
                  id: 'policies.list.wizard.holder.immigrationStatus',
                  defaultMessage: 'Immigration Status',
                })}
              >
                <option value=''>
                  {intl.formatMessage({id: 'common.placeholder.selectOption', defaultMessage: 'Select an option'})}
                </option>
                {translatedImmigrationStatuses(intl).map((option) => (
                  <option value={option.value} key={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col lg={6} sm={12} md={4}>
            <Form.Group>
              <Form.Label htmlFor='immigrationStatusCode'>
                <FormattedMessage
                  id='policies.detail.form.member.legal.immigrationCategory'
                  defaultMessage='Immigration status category'
                />
              </Form.Label>
              <Form.Control
                id='immigrationStatusCode'
                name='immigrationStatusCode'
                value={values.immigrationStatusCode?? ''}
                onChange={(e) => handleChange('immigrationStatusCode', e.target.value)}
                // onChange={(e) => {
                //   if (membernum === 0) handleChange('holder.immigrationStatusCode', e.target.value)
                //   else handleChange(`member[${membernum}].immigrationStatusCode`, e.target.value)
                // }}
                placeholder={intl.formatMessage({id: 'policies.detail.form.member.legal.immigrationCategoryPlaceholder', defaultMessage: 'e.g., C08'})}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col lg={6} sm={12} md={4}>
            <Form.Label htmlFor='uscis'>
              <FormattedMessage id='policies.detail.form.member.legal.uscis' defaultMessage='USCIS #' />
            </Form.Label>
            <InputGroup className='mb-3'>
              <Form.Control
                id='uscis'
                placeholder={intl.formatMessage({id: 'policies.detail.form.member.legal.uscisPlaceholder', defaultMessage: 'US Citizenship and Immigration Services'})}
                name='USCIS'
                value={values.uscis?? ''}
                onChange={(e) => handleChange('uscis', e.target.value)}
                // onChange={(e) => {
                //   if (membernum === 0) handleChange('holder.uscis', e.target.value)
                //   else handleChange(`member[${membernum}].uscis`, e.target.value)
                // }}
              />
            </InputGroup>
          </Col>
          <Col lg={6} sm={12} md={4}>
            <Form.Label htmlFor='passport'>
              <FormattedMessage id='policies.detail.form.member.legal.passport' defaultMessage='Passport #' />
            </Form.Label>
            <InputGroup className='mb-3'>
              <Form.Control
                id='passport'
                name='passport'
                placeholder={intl.formatMessage({id: 'policies.detail.form.member.legal.passportPlaceholder', defaultMessage: 'Passport number'})}
                value={values.passport?? ''}
                onChange={(e) => handleChange('passport', e.target.value)}
                // onChange={(e) => {
                //   if (membernum === 0) handleChange('holder.passport', e.target.value)
                //   else handleChange(`member[${membernum}].passport`, e.target.value)
                // }}
              />
            </InputGroup>
          </Col>
        </Row>
        <Row>
          <Col lg={6} sm={12} md={4}>
            <Form.Label htmlFor='ssn'>
              <FormattedMessage id='policies.detail.form.member.legal.ssn' defaultMessage='Social Security Number (SSN)' />
            </Form.Label>
            <InputGroup className='mb-3'>
              <Form.Control
                placeholder={intl.formatMessage({id: 'policies.detail.form.member.legal.ssnPlaceholder', defaultMessage: 'Social security number'})}
                name='ssn'
                id='ssn'
                value={values.ssn?? ''}
                onChange={(e) => handleChange('ssn', e.target.value)}
                // onChange={(e) => {
                //   if (membernum === 0) handleChange('holder.ssn', e.target.value)
                //   else handleChange(`member[${membernum}].ssn`, e.target.value)
                // }}
              />
              <button type='button' className='btn btn-outline-secondary border' id='button-addon2'>
                <i className='bi bi-eye-fill' />
              </button>
            </InputGroup>
          </Col>
          <Col lg={6} sm={12} md={4}>
            <Form.Group>
              <DateInput
                id="ssnIssuedDate"
                name="ssnIssuedDate"
                label={intl.formatMessage({id: 'policies.detail.form.member.legal.ssnIssuedDate', defaultMessage: 'SSN issued date'})}
                value={values.ssnIssuedDate}
                disabled={!values.ssn}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col lg={4} sm={12} md={4}>
            <Form.Label htmlFor='greenCard'>
              <FormattedMessage id='policies.detail.form.member.legal.greenCard' defaultMessage='Green card #' />
            </Form.Label>
            <InputGroup className='mb-3'>
              <Form.Control
                placeholder={intl.formatMessage({id: 'policies.detail.form.member.legal.greenCardPlaceholder', defaultMessage: 'Green card number'})}
                name='greenCard'
                id='greenCard'
                value={values.greenCard?? ''}
                onChange={(e) => handleChange('greenCard', e.target.value)}
                // onChange={(e) => {
                //   if (membernum === 0) handleChange('holder.greenCard', e.target.value)
                //   else handleChange(`member[${membernum}].greenCard`, e.target.value)
                // }}
              />
            </InputGroup>
          </Col>
          <Col lg={4} sm={12} md={4}>
            <DateInput
              id="greenCardIssuedDate"
              name="greenCardIssuedDate"
              label={intl.formatMessage({id: 'policies.detail.form.member.legal.greenCardIssueDate', defaultMessage: 'Green card issue date'})}
              value={values.greenCardIssuedDate}
              disabled={!values.greenCard}
              onChange={handleChange}
            />
          </Col>
          <Col lg={4} sm={12} md={4}>
            <DateInput
              id="greenCardExpirationDate"
              name="greenCardExpirationDate"
              label={intl.formatMessage({id: 'policies.detail.form.member.legal.greenCardExpirationDate', defaultMessage: 'Green card expiration date'})}
              value={values.greenCardExpirationDate}
              disabled={!values.greenCard}
              onChange={handleChange}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={4} sm={12} md={4}>
            <Form.Group>
              <Form.Label htmlFor='workPermitCard'>
                <FormattedMessage id='policies.detail.form.member.legal.workPermit' defaultMessage='Work permit card #' />
              </Form.Label>
              <InputGroup className='mb-3'>
                <Form.Control
                  placeholder={intl.formatMessage({id: 'policies.detail.form.member.legal.workPermitPlaceholder', defaultMessage: 'Permit card number'})}
                  name='workPermitCard'
                  id='workPermitCard'
                  value={values.workPermitCard?? ''}
                  onChange={(e) => handleChange('workPermitCard', e.target.value)}
                />
              </InputGroup>
            </Form.Group>
          </Col>
          <Col lg={4} sm={12} md={4}>
            <DateInput
              id="workPermitIssuedDate"
              name="workPermitIssuedDate"
              label={intl.formatMessage({id: 'policies.detail.form.member.legal.workPermitIssueDate', defaultMessage: 'Work permit issue date'})}
              value={values.workPermitIssuedDate}
              disabled={!values.workPermitCard}
              onChange={handleChange}
            />
          </Col>
          <Col lg={4} sm={12} md={4}>
            <DateInput
              id="workPermitExpirationDate"
              name="workPermitExpirationDate"
              label={intl.formatMessage({id: 'policies.detail.form.member.legal.workPermitExpirationDate', defaultMessage: 'Work permit expiration date'})}
              value={values.workPermitExpirationDate}
              disabled={!values.workPermitCard}
              onChange={handleChange}
            />
          </Col>
        </Row>
        <div className='mb-3 form-group form-group-md row'>
          <div className='col-lg-6'>
            <Form.Label htmlFor='driverLicense'>
              <FormattedMessage id='policies.detail.form.member.legal.driverLicense' defaultMessage='Driver license #' />
            </Form.Label>
            <InputGroup className='mb-3'>
              <Form.Control
                placeholder={intl.formatMessage({id: 'policies.detail.form.member.legal.driverLicensePlaceholder', defaultMessage: 'Driver license number'})}
                name='driverLicense'
                id='driverLicense'
                value={values.driverLicense?? ''}
                onChange={(e) => handleChange('driverLicense', e.target.value)}
              />
            </InputGroup>
          </div>
          <div className='col-lg-6'>
            <DateInput
              id="driverLicenseExpirationDate"
              name="driverLicenseExpirationDate"
              label={intl.formatMessage({id: 'policies.detail.form.member.legal.driverLicenseExpirationDate', defaultMessage: 'Driver license expiration date'})}
              value={values.driverLicenseExpirationDate}
              disabled={!values.driverLicense}
              onChange={handleChange}
            />
          </div>
        </div>
        <Row>
          <Col lg={4} sm={12} md={4}>
            <Form.Label htmlFor='medicareNumber'>
              <FormattedMessage id='policies.detail.form.member.legal.medicareNumber' defaultMessage='Medicare number' />
            </Form.Label>
            <InputGroup className='mb-3'>
              <Form.Control
                placeholder={intl.formatMessage({id: 'policies.detail.form.member.legal.medicareNumber', defaultMessage: 'Medicare number'})}
                name='medicareNumber'
                id='medicareNumber'
                value={values.medicareNumber ?? ''}
                onChange={(e) => handleChange('medicareNumber', e.target.value)}
              />
            </InputGroup>
          </Col>
          <Col lg={4} sm={12} md={4}>
            <DateInput
              id="medicarePartAStartDate"
              name="medicarePartAStartDate"
              label={intl.formatMessage({id: 'policies.detail.form.member.legal.medicarePartA', defaultMessage: 'Part A Start Date'})}
              value={values.medicarePartAStartDate}
              disabled={!values.medicareNumber}
              onChange={handleChange}
            />
          </Col>
          <Col lg={4} sm={12} md={4}>
            <DateInput
              id="medicarePartBStartDate"
              name="medicarePartBStartDate"
              label={intl.formatMessage({id: 'policies.detail.form.member.legal.medicarePartB', defaultMessage: 'Part B Start Date'})}
              value={values.medicarePartBStartDate}
              disabled={!values.medicareNumber}
              onChange={handleChange}
            />
          </Col>
        </Row>
      </div>
    </Accordion.Body>
  </Accordion.Item>
  )
}
